import { t, type StringKey } from '../strings';
import { BUDGET_TOO_LOW_ACTION_ID, type DealValueSource } from './dealValueSource';

/**
 * 🔒 The Deal page's Next Action banner (decision 1219): the first action named
 * as a sentence with its reason or its Due, the rest counted, and Deal Progress
 * named as where they are listed. An Urgent action's sentence leads *Urgent:*
 * (decision 1485). It is a visual element: it writes no audit
 * row and no error-log row, and its dismissal lives in the browser alone.
 */
export interface NextActionBannerRow {
  /** The sub-stage key, or the budget talk's own id (decision 0726). */
  id?: string | null;
  label: string;
  /** The row's Due as a calendar date, where one is set. */
  dueDate?: string | null;
}

/**
 * 🔒 A next action has two levels (decision 1485): Urgent is the budget talk and a
 * first action whose Due has passed, Normal is everything else. The level is read
 * off the first row alone, and the dismissal rule does not depend on it.
 */
export type NextActionLevel = 'normal' | 'urgent';

export function nextActionLevel(rows: readonly NextActionBannerRow[], today: string): NextActionLevel | null {
  const first = rows[0];
  if (!first) return null;
  if (first.id === BUDGET_TOO_LOW_ACTION_ID) return 'urgent';
  return first.dueDate && first.dueDate < today ? 'urgent' : 'normal';
}

/** The counted rest reads as a word up to nine, the way a sentence would say it. */
const COUNT_WORDS: Readonly<Record<number, StringKey>> = {
  2: 'deal.nextBanner.two',
  3: 'deal.nextBanner.three',
  4: 'deal.nextBanner.four',
  5: 'deal.nextBanner.five',
  6: 'deal.nextBanner.six',
  7: 'deal.nextBanner.seven',
  8: 'deal.nextBanner.eight',
  9: 'deal.nextBanner.nine',
};

/**
 * The banner's words, or null where nothing is open and no banner draws.
 * `today` is the viewer's calendar day, which decides whether the Due has
 * passed; `formatDay` writes a date the way the organisation writes dates.
 */
export function nextActionBannerText(rows: readonly NextActionBannerRow[], today: string, formatDay: (day: string) => string): string | null {
  const first = rows[0];
  if (!first) return null;
  const firstSentence = first.id === BUDGET_TOO_LOW_ACTION_ID
    ? t('deal.nextBanner.budget')
    : !first.dueDate
      ? `${t('deal.nextBanner.lead')} ${first.label}, ${t('deal.nextBanner.noDue')}`
      : `${t('deal.nextBanner.lead')} ${first.label}, ${t(first.dueDate < today ? 'deal.nextBanner.wasDue' : 'deal.nextBanner.due')} ${formatDay(first.dueDate)}.`;
  const rest = rows.length - 1;
  const lead = nextActionLevel(rows, today) === 'urgent' ? `${t('deal.nextBanner.urgent')} ` : '';
  if (rest === 0) return `${lead}${firstSentence}`;
  const counted = rest === 1 ? t('deal.nextBanner.oneMore') : `${COUNT_WORDS[rest] ? t(COUNT_WORDS[rest]!) : String(rest)} ${t('deal.nextBanner.moreOpen')}`;
  return `${lead}${firstSentence} ${counted}`;
}

/**
 * 🔒 What a dismissal is remembered against (decision 1219): the first action's
 * name and its Due, so the banner returns when either changes and not for a
 * change lower in the list.
 */
export function nextActionBannerSignature(rows: readonly NextActionBannerRow[]): string | null {
  const first = rows[0];
  return first ? `${first.id ?? first.label}|${first.dueDate ?? ''}` : null;
}

/** The browser key a dismissal is kept under: one per person per Deal (decision 1219). */
export const NEXT_ACTION_BANNER_KEY_PREFIX = 'bms2.dealNextActionDismissed.';

export function nextActionBannerKey(personId: string, dealId: string): string {
  return `${NEXT_ACTION_BANNER_KEY_PREFIX}${personId}.${dealId}`;
}

/**
 * 🔒 The one line of the Deal Value help that changes from Deal to Deal (decision
 * 1219): where this Deal's value came from.
 */
export const DEAL_VALUE_SOURCE_HELP: Readonly<Record<DealValueSource, StringKey>> = {
  quote: 'help.dealValue.thisQuote',
  bom: 'help.dealValue.thisBom',
  budget: 'help.dealValue.thisBudget',
  entered: 'help.dealValue.thisEntered',
  requestMinimum: 'help.dealValue.thisRequestMinimum',
  invoiced: 'help.dealValue.thisInvoiced',
};
